import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { type EnhancedToolResult, type ToolExecutionContext } from '../types';

import { applyEdits } from './applyEdits';
import { DesignTool, designTargetSchemaFields } from './designTargeting';
import { applyHtmlAndLint, checkpointDesign } from './htmlAssetToolHelpers';

import { getHtmlDesignPlan, getSceneTechniques } from 'js/src/data/html-design';

export const editHtmlAssetSchema = z.object({
    ...designTargetSchemaFields,
    edits: z
        .array(
            z.object({
                old: z
                    .string()
                    .describe(
                        'Substring to find in the current composition. ' +
                            'Must match exactly once. Include enough surrounding ' +
                            'context (attributes, neighboring tags) to be unique.',
                    ),
                new: z.string().describe('Replacement text. May be empty to delete.'),
            }),
        )
        .min(1)
        .describe(
            'Sequential list of edits to apply. Each edit replaces the ' +
                'first occurrence of `old` with `new`.',
        ),
});
export type EditHtmlAssetArgs = z.infer<typeof editHtmlAssetSchema>;

/**
 * Makes surgical find-and-replace edits to the active HtmlAsset's composition instead of
 * re-authoring the whole document. Reads the current HTML from the asset, applies the edits
 * atomically (all match or nothing commits), then writes and re-lints via the shared helper. The
 * find/replace engine (whitespace/`<br>`-tolerant matching, superseded-edit detection, divergence
 * hints on a miss) lives in applyEdits.
 */
export class EditHtmlAssetTool extends DesignTool<typeof editHtmlAssetSchema> {
    protected override readonly schema = editHtmlAssetSchema;
    protected override readonly toolName = TOOL_NAMES.editHtmlAsset;
    protected override readonly toolDescription =
        'Make surgical edits to the current composition by find-and-replace. ' +
        'Default to this for any change to an existing design — it costs far fewer ' +
        `tokens than ${TOOL_NAMES.writeHtmlAsset} and is less likely to drop unrelated parts of ` +
        "the document. Each edit's `old` must match exactly once in the current " +
        'composition; if it matches zero or multiple times, the call fails and no ' +
        'change is committed. When a call fails, the whole batch is validated first, ' +
        'so EVERY failed edit is reported at once and the composition is left ' +
        'UNCHANGED — fix every reported edit and resend; never build a follow-up batch ' +
        "assuming part of a rejected call applied. Edits apply in order, so a later edit's " +
        '`old` can target text introduced by an earlier edit. Use ONE edit per region — ' +
        'a later edit whose `old` was already changed by an earlier one is reported as ' +
        'superseded and skipped, not applied. Whitespace and `<br>` breaks are tolerated, ' +
        'but other text, tags, and entities must match exactly — match against the ' +
        'composition you authored, or against ' +
        `${TOOL_NAMES.readHtmlAsset} if you no longer have it. ` +
        `Returns lint findings; if any are errors, fix them and call ${TOOL_NAMES.editHtmlAsset} ` +
        'again before replying to the user.';

    public getToolDisplayName(): string {
        return 'Edit design';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: EditHtmlAssetArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveTarget(args.scene_id);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset, scene } = resolved;

        const { html, skipped, superseded, errors } = applyEdits(asset.html, args.edits);

        if (errors.length > 0) {
            // The whole batch is validated before anything commits, so EVERY failed edit is reported
            // here — fix them all in one resend rather than discovering them one rejected call at a
            // time. The leading line is emphatic that nothing changed: the common failure mode is the
            // agent building its next batch on the assumption that some of a rejected batch's edits
            // had landed, then editing text that only ever existed in the rejected call.
            const failed = new Set(errors.map((e) => e.index));
            const matchedIndices = args.edits
                .map((_, i) => i)
                .filter((i) => !failed.has(i) && !skipped.includes(i) && !superseded.includes(i));
            const lines = [
                `Failed to apply ${errors.length} of ${args.edits.length} edit(s). NOTHING was ` +
                    `committed — ${TOOL_NAMES.editHtmlAsset} is atomic, so the composition is UNCHANGED from ` +
                    'before this call. Do not assume any edit in this batch (even the ones that ' +
                    'matched) landed. Fix every failed edit below and resend. If your copy of ' +
                    `the composition has drifted, call ${TOOL_NAMES.readHtmlAsset} to re-read it first.`,
            ];
            for (const e of errors) {
                lines.push(
                    e.context
                        ? `\n• edit #${e.index + 1}: ${e.reason}\n${e.context}`
                        : `\n• edit #${e.index + 1}: ${e.reason}`,
                );
            }
            if (matchedIndices.length > 0) {
                lines.push(
                    `\nEdits #${matchedIndices
                        .map((i) => i + 1)
                        .join(
                            ', #',
                        )} matched the composition as it is now, but fixing the failed edits above can shift the surrounding text — re-check them against the current composition before you resend.`,
                );
            }
            return { result: lines.join('\n'), isSuccess: false };
        }

        // Every edit was a no-op: nothing changed, so don't re-lint/write.
        if (skipped.length === args.edits.length) {
            return {
                result: `No changes applied — all ${args.edits.length} edit(s) were no-ops (old === new).`,
                isSuccess: false,
            };
        }

        const outcome = await applyHtmlAndLint(
            this.luminary,
            asset,
            html,
            scene.duration,
            context.conversationId,
            getHtmlDesignPlan(this.getState(), scene.id),
            getSceneTechniques(this.getState(), scene.id),
        );
        checkpointDesign(this.luminary, this.dispatch, context.parentActionGroup);
        const notes: string[] = [];
        if (superseded.length > 0) {
            notes.push(
                `Note: skipped ${superseded.length} superseded edit(s) — their \`old\` was already ` +
                    `changed by an earlier edit in this batch, so they were redundant: #${superseded
                        .map((i) => i + 1)
                        .join(', #')}.`,
            );
        }
        if (skipped.length > 0) {
            notes.push(
                `Note: skipped ${skipped.length} no-op edit(s) (old === new): #${skipped.map((i) => i + 1).join(', #')}.`,
            );
        }
        if (notes.length > 0) {
            return { ...outcome, result: `${notes.join('\n')}\n${outcome.result}` };
        }
        return outcome;
    }
}
